import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CHANNEL_LABELS = {
  shopify: "Shopify", amazon: "Amazon", flipkart: "Flipkart",
  offline: "Offline / B2B", other: "Other",
};

export default function ChannelBreakdown({ workspaceId, dateFrom, dateTo, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workspaceId || !dateFrom || !dateTo) return;
    setLoading(true);
    api
      .get("/orders/channels-summary", { params: { workspace_id: workspaceId, date_from: dateFrom, date_to: dateTo } })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [workspaceId, dateFrom, dateTo, refreshKey]);

  const totals = rows.reduce(
    (a, r) => ({ revenue: a.revenue + r.revenue, orders: a.orders + r.orders, units: a.units + r.units, cogs: a.cogs + r.cogs, contribution: a.contribution + r.contribution }),
    { revenue: 0, orders: 0, units: 0, cogs: 0, contribution: 0 }
  );

  if (loading) {
    return <div className="border border-border p-6 text-sm text-muted-foreground">Loading channel breakdown…</div>;
  }
  if (!rows.length) {
    return (
      <div className="border border-border p-6" data-testid="channel-breakdown-empty">
        <div className="overline text-muted-foreground">Sales Channel Breakdown</div>
        <div className="mt-2 text-sm text-muted-foreground">No orders imported for this range yet. Use "Upload CSV" to import Shopify / Amazon / Flipkart exports.</div>
      </div>
    );
  }

  return (
    <div className="border border-border" data-testid="channel-breakdown">
      <div className="p-4 border-b border-border flex items-baseline justify-between">
        <div>
          <div className="overline text-muted-foreground">Multi-channel</div>
          <div className="font-display text-base font-semibold">Sales Channel Breakdown</div>
        </div>
        <div className="text-xs text-muted-foreground font-mono">{dateFrom} → {dateTo}</div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="overline">Channel</TableHead>
            <TableHead className="overline text-right">Orders</TableHead>
            <TableHead className="overline text-right">Units</TableHead>
            <TableHead className="overline text-right">Gross</TableHead>
            <TableHead className="overline text-right">Discount</TableHead>
            <TableHead className="overline text-right">Net Revenue</TableHead>
            <TableHead className="overline text-right">COGS</TableHead>
            <TableHead className="overline text-right">Contribution</TableHead>
            <TableHead className="overline text-right">Rev share</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const share = totals.revenue > 0 ? (r.revenue / totals.revenue) * 100 : 0;
            return (
              <TableRow key={r.channel} data-testid={`channel-row-${r.channel}`}>
                <TableCell>
                  <span className="uppercase font-mono text-xs px-2 py-0.5 border border-border">{CHANNEL_LABELS[r.channel] || r.channel}</span>
                </TableCell>
                <TableCell className="text-right font-mono">{r.orders}</TableCell>
                <TableCell className="text-right font-mono">{r.units}</TableCell>
                <TableCell className="text-right font-mono">{inr(r.gross, { decimals: 0 })}</TableCell>
                <TableCell className="text-right font-mono text-[hsl(var(--negative))]">{inr(r.discounts, { decimals: 0 })}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{inr(r.revenue, { decimals: 0 })}</TableCell>
                <TableCell className="text-right font-mono">{inr(r.cogs, { decimals: 0 })}</TableCell>
                <TableCell className={`text-right font-mono ${r.contribution >= 0 ? "text-[hsl(var(--positive))]" : "text-[hsl(var(--negative))]"}`}>{inr(r.contribution, { decimals: 0 })}</TableCell>
                <TableCell className="text-right font-mono">{share.toFixed(1)}%</TableCell>
              </TableRow>
            );
          })}
          <TableRow className="border-t-2 border-foreground/60">
            <TableCell className="overline">Total</TableCell>
            <TableCell className="text-right font-mono font-semibold">{totals.orders}</TableCell>
            <TableCell className="text-right font-mono font-semibold">{totals.units}</TableCell>
            <TableCell className="text-right font-mono font-semibold">—</TableCell>
            <TableCell className="text-right font-mono font-semibold">—</TableCell>
            <TableCell className="text-right font-mono font-semibold">{inr(totals.revenue, { decimals: 0 })}</TableCell>
            <TableCell className="text-right font-mono font-semibold">{inr(totals.cogs, { decimals: 0 })}</TableCell>
            <TableCell className={`text-right font-mono font-semibold ${totals.contribution >= 0 ? "text-[hsl(var(--positive))]" : "text-[hsl(var(--negative))]"}`}>{inr(totals.contribution, { decimals: 0 })}</TableCell>
            <TableCell className="text-right font-mono font-semibold">100%</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
